import { inject, Injectable, Provider } from '@angular/core';
import { Observable, Observer, Subscription, } from 'rxjs';
import {
  EShoppingCartGetQueryType, ProductIdentifierDto, ShoppingCartSetItemDto, ShoppingCartClient,
  ShoppingCartCreateByTemplateCommand, ShoppingCartCreateByTemplateCommandResult, ShoppingCartDeleteCommand,
  ShoppingCartGetQuery, ShoppingCartGetQueryResult, ShoppingCartItemDeleteCommand, ShoppingCartItemSetCommand,
  ShoppingCartSaveAsTemplateCommand, ShoppingCartSaveAsTemplateCommandResult, ShoppingCartSetOrderDataCommand,
  ShoppingCartSetOrderDataCommandResult, ShoppingCartTemplateAddCommand, ShoppingCartTemplateListGetQuery,
  ShoppingCartDeleteCommandResult, ShoppingCartItemDeleteCommandResult, ShoppingCartItemSetCommandResult,
  ShoppingCartValidateCommand, ShoppingCartValidateCommandResult, ShoppingCartTemplateListGetQueryResult,
} from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { IShoppingCartApiService, IShoppingCartApiServiceToken } from './shopping-cart-api-service.interface';
import { IComparisonService, IComparisonServiceToken } from '../../infrastructure/comparison/comparison-service.interface';
import { ILoggerService, ILoggerServiceToken } from '../../infrastructure/logger/logger-service.interface';

@Injectable({
  providedIn: 'root'
})
export class ShoppingCartApiService implements IShoppingCartApiService {

  private readonly logger: ILoggerService;
  private readonly comparisonService: IComparisonService;

  private shoppingCartGetSubscription: Subscription | undefined;

  private lastShoppingCartGetQuery: ShoppingCartGetQuery | undefined;

  constructor(
    private shoppingCartClient: ShoppingCartClient,
  ) {
    this.logger = inject(ILoggerServiceToken);
    this.comparisonService = inject(IComparisonServiceToken);
  }

  public shoppingCartGet(
    shoppingCartGetQueryType: EShoppingCartGetQueryType,
    validateCart: boolean, cartNumber?: number | undefined,
    useCancellationLogic: boolean | undefined = true): Observable<ShoppingCartGetQueryResult> {
    this.logger.trace(`shoppingCartGet called. shoppingCartGetQueryType(${shoppingCartGetQueryType}), validateCart(${validateCart}), cartNumber(${cartNumber})`, this.constructor.name);

    let query = new ShoppingCartGetQuery();
    query.shoppingCartGetQueryType = shoppingCartGetQueryType;
    query.cartNumber = cartNumber;
    query.validateCart = validateCart;

    if (useCancellationLogic
      && this.shoppingCartGetSubscription != undefined
      && this.comparisonService.equalsGenericDeepSlow(this.lastShoppingCartGetQuery, query)
    ) {
      this.shoppingCartGetSubscription.unsubscribe();
    }

    this.lastShoppingCartGetQuery = query;

    return new Observable((observer: Observer<ShoppingCartGetQueryResult>) => {
      this.shoppingCartGetSubscription = this.shoppingCartClient.shoppingCartGet(query).subscribe(result => {
        observer.next(result);
        observer.complete();
      },
        error => {
          observer.error(error);
        }
      );
    });
  }

  public shoppingCartTemplateListGet(): Observable<ShoppingCartTemplateListGetQueryResult> {
    this.logger.trace(`shoppingCartTemplateListGet called.`, this.constructor.name);
    let query = new ShoppingCartTemplateListGetQuery();
    return this.shoppingCartClient.shoppingCartTemplateListGet(query);
  }

  public shoppingCartSaveAsTemplate(cartNumber: number, targetCartName: string): Observable<ShoppingCartSaveAsTemplateCommandResult> {
    this.logger.trace(`shoppingCartSaveAsTemplate called. cartNumber(${cartNumber}), targetCartName(${targetCartName})`, this.constructor.name);
    let command = new ShoppingCartSaveAsTemplateCommand({
      cartNumber: cartNumber,
      targetCartName: targetCartName,
    });
    return this.shoppingCartClient.shoppingCartSaveAsTemplate(command);
  }

  public shoppingCartCreateByTemplate(cartNumber: number): Observable<ShoppingCartCreateByTemplateCommandResult> {
    this.logger.trace(`shoppingCartCreateByTemplate called. cartNumber(${cartNumber})`, this.constructor.name);
    let command = new ShoppingCartCreateByTemplateCommand({
      cartNumber: cartNumber,
    });

    return this.shoppingCartClient.shoppingCartCreateByTemplate(command);
  }

  public shoppingCartTemplateAdd(templateName: string, itemList?: ShoppingCartSetItemDto[] | undefined) {
    this.logger.trace(`shoppingCartTemplateAdd called. templateName(${templateName})`, this.constructor.name);
    let command = new ShoppingCartTemplateAddCommand({
      templateName: templateName,
      itemList: itemList
    });

    return this.shoppingCartClient.shoppingCartTemplateAdd(command);
  }

  public shoppingCartDelete(cartNumber: number): Observable<ShoppingCartDeleteCommandResult> {
    this.logger.trace(`shoppingCartDelete called. cartNumber(${cartNumber})`, this.constructor.name);
    let shoppingCartDeleteCommand = new ShoppingCartDeleteCommand({
      cartNumber: cartNumber,
    });

    return this.shoppingCartClient.shoppingCartDelete(shoppingCartDeleteCommand);
  }

  public shoppingCartItemDelete(productIdentifierList: ProductIdentifierDto[], cartNumber: number, validateCartAfterwards?: boolean | undefined): Observable<ShoppingCartItemDeleteCommandResult> {
    this.logger.trace(`shoppingCartItemDelete called. cartNumber(${cartNumber}), validateCartAfterwards(${validateCartAfterwards})`, this.constructor.name);
    let command = new ShoppingCartItemDeleteCommand({
      cartNumber: cartNumber,
      itemList: productIdentifierList,
      validateCartAfterwards: validateCartAfterwards == undefined ? false : validateCartAfterwards
    });

    return this.shoppingCartClient.shoppingCartItemDelete(command);
  }

  public shoppingCartItemSet(shoppingCartSetItemList: ShoppingCartSetItemDto[], cartNumber: number, deliveryDate: Date, validateCartAfterwards?: boolean | undefined): Observable<ShoppingCartItemSetCommandResult> {
    this.logger.trace(`shoppingCartItemSet called. cartNumber(${cartNumber}), deliveryDate(${deliveryDate}),  validateCartAfterwards(${validateCartAfterwards})`, this.constructor.name);
    let command = new ShoppingCartItemSetCommand({
      cartNumber: cartNumber,
      deliveryDate: deliveryDate,
      itemList: shoppingCartSetItemList,
      validateCartAfterwards: validateCartAfterwards == undefined ? false : validateCartAfterwards
    });

    return this.shoppingCartClient.shoppingCartItemSet(command);
  }

  public shoppingCartSetOrderData(command: ShoppingCartSetOrderDataCommand): Observable<ShoppingCartSetOrderDataCommandResult> {
    this.logger.trace(`shoppingCartSetOrderData called.`, this.constructor.name);
    return this.shoppingCartClient.shoppingCartSetOrderData(command);
  }

  public shoppingCartValidate(cartNumber: number): Observable<ShoppingCartValidateCommandResult> {
    this.logger.trace(`shoppingCartValidate called. cartNumber(${cartNumber})`, this.constructor.name);
    let command = new ShoppingCartValidateCommand({ cartNumber: cartNumber });
    return this.shoppingCartClient.shoppingCartValidate(command);
  }

}


/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const ShoppingCartApiServiceProvider: Provider[] = [
  { provide: ShoppingCartClient },
  { provide: IShoppingCartApiServiceToken, useExisting: ShoppingCartApiService }
];
